/**
 * @ Author: Housefolios
 * @ Create Time: 2025-07-08 17:47:00
 * @ Modified by: David Helmick
 * @ Modified time: 2025-07-08 18:25:12
 * @ Description: Routes for when you are signed in
 */

import React, { Suspense, lazy, useEffect } from 'react'
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  Outlet,
  useNavigate,
} from 'react-router'

import { identifyUser } from '@/analytics/mixpanel'

// Layout Blueprints

import { LeftSidebar, MinimalLayout } from '../../layout-blueprints'

const AssumptionsSettingsPage = lazy(
  () => import('../../housefolios-pages/AssumptionsSettings'),
)
import BillingSettingsPage from '../../housefolios-pages/BillingSettings'
const LaunchpadPage = lazy(
  () => import('../../housefolios-pages/Launchpad'),
)
const ToolsPage = lazy(() => import('../../housefolios-pages/Tools'))
const MarketplacePage = lazy(
  () => import('../../housefolios-pages/Marketplace'),
)
const MarketplaceSettingsPage = lazy(
  () => import('../../housefolios-pages/MarketplaceSettings'),
)
const ReportsPage = lazy(
  () => import('../../housefolios-pages/Reports'),
)
const OrganizationSettingsPage = lazy(
  () => import('../../housefolios-pages/OrganizationSettings'),
)
const PersonalSettingsPage = lazy(
  () => import('../../housefolios-pages/PersonalSettings'),
)
// import PortfolioPage from '../../housefolios-pages/Portfolio/'
const PropertiesPage = lazy(
  () => import('../../housefolios-pages/Properties'),
)
const BoardPage = lazy(() => import('../../housefolios-pages/Board'))
const PropertyPage = lazy(
  () => import('../../housefolios-pages/Property'),
)
import PropertySearchUnavailablePage from '../../housefolios-pages/PropertySearchUnavailable'
const TrashPage = lazy(() => import('../../housefolios-pages/Trash'))
const TeamSettingsPage = lazy(
  () => import('../../housefolios-pages/TeamSettings'),
)
// const IntegrationsPage = lazy(
//   () => import('../../housefolios-pages/Integrations'),
// )
const SharedPortfolio = lazy(
  () => import('../../housefolios-pages/SharedPortfolio'),
)
const SharedMarketplace = lazy(
  () => import('../../housefolios-pages/SharedMarketplace'),
)
const SharedProperty = lazy(
  () => import('../../housefolios-pages/SharedProperty'),
)
const SharedProperties = lazy(
  () => import('../../housefolios-pages/SharedProperties'),
)
const PDFContainer = lazy(
  () =>
    import(
      '../../housefolios-components/PropertyContainer/Proforma/pdfContainer'
    ),
)
const TrainingPage = lazy(
  () => import('../../housefolios-pages/TrainingPage'),
)
const LotsPage = lazy(() => import('../../housefolios-pages/Lots'))
import DelinquentPage from '@/housefolios-components/zComponents/DelinquentPage'
import DeactivatedPage from '@/housefolios-components/zComponents/DeactivatedPage'
import AffiliateDeactivatedPage from '@/housefolios-components/zComponents/AffiliateDeactivatedPage'
const Notifications = lazy(
  () => import('../../housefolios-pages/Notifications'),
)

const LotPage = lazy(() => import('../../housefolios-pages/Lot'))
const CommercialPage = lazy(
  () => import('../../housefolios-pages/Commercial'),
)
const PartnerPortal = lazy(
  () => import('../../housefolios-pages/PartnerPortal'),
)
const RealeflowAuditPage = lazy(
  () =>
    import('../../housefolios-pages/AffiliateAdmin/RealeflowAudit'),
)
import CoachingOffer from '@/housefolios-pages/coachingOffer'
const HouseFoliosAdminClientPage = lazy(
  () =>
    import('../../housefolios-pages/HouseFoliosAdmin/ClientSupport'),
)
const AffiliateAdminClientPage = lazy(
  () =>
    import('../../housefolios-pages/AffiliateAdmin/ClientSupport'),
)
const MissingGhlContactPage = lazy(
  () =>
    import(
      '../../housefolios-pages/AffiliateAdmin/MissingGhlContact'
    ),
)
const FeatureRequest = lazy(
  () => import('../../housefolios-pages/FeatureRequest'),
)
const SupportChatPage = lazy(
  () => import('../../housefolios-pages/SupportChatPage'),
)
import RealeflowRedirect from '@/housefolios-components/Routes/realeflowRedirect'
import ProformaRedirect from '../../housefolios-components/Routes/ProformaRedirect'
import Legal from '@/housefolios-pages/Legal'
import Terms from '@/housefolios-pages/Terms'
import Privacy from '@/housefolios-pages/Privacy'

import DealAnalysisPage from '../../housefolios-pages/DealAnalysis'
import { LoadingSkeleton } from '@/housefolios-components/LoadingSkeleton'
const DealSauceBuyBoxPage = lazy(
  () => import('../../housefolios-pages/DealSauceBuyBox'),
)
import CommercialProperty from '../../housefolios-components/CommercialProperty'
import ArticlePage from '../../housefolios-pages/ArticlePage'
import HouseFoliosAdminPage from '../../housefolios-pages/HouseFoliosAdmin/index.jsx'
import ContactsPage from '@/housefolios-pages/Contacts'
import SuspenseLoading from '@/housefolios-components/Routes/SuspenseLoading'

const SignedIn = (props) => {
  const {
    me,
    affiliate,
    noOrg,
    delinquent,
    defaultPage,
    setOpenAnalyzeAreaTour,
    setOpenAnalyzeCompsTour,
    setOpenChooseStrategyTour,
    setOpenFeedbackTour,
    setOpenTakeActionTour,
    setOpenTour,
    setOpenVerifyFinancialsTour,
  } = props

  const location = useLocation()

  const navigate = useNavigate()

  // Defensive: during logout/session transitions `me` can briefly be nullish
  // while this component is still mounted.
  const activeOrg = me?.activeOrg
  const memberNorm = String(activeOrg?.member || '').toLowerCase()
  const isPropertySearchRetired = [
    'pacemorby',
    'astroflip',
    'pep',
  ].includes(memberNorm)

  // Non-Realeflow orgs and retired Realeflow affiliates: Property Search
  // shows the unavailable page. Remaining Realeflow orgs keep the iframe.

  // Identify the signed-in user once we have `me`.
  useEffect(() => {
    identifyUser(me)
  }, [me?._id, me?.activeOrg?._id])

  return noOrg ? (
    <Routes>
      <Route element={<MinimalLayout />}>
        <Route
          path="/deactivated"
          element={
            ['pacemorby', 'astroflip', 'pep'].includes(
              (me?.activeOrg?.member || '').toLowerCase(),
            ) ? (
              <AffiliateDeactivatedPage />
            ) : (
              <DeactivatedPage />
            )
          }
        />
      </Route>
      <Route path="/*" element={<Navigate to="/deactivated" />} />
    </Routes>
  ) : delinquent ? (
    <Routes>
      {/* Delinquent lock: billing recovery, legal pages, and logout only. */}
      <Route element={<MinimalLayout />}>
        <Route path="/delinquent" element={<DelinquentPage />} />
      </Route>
      <Route
        path="/legal"
        element={<Legal affiliate={affiliate?.memberValue} />}
      />
      <Route
        path="/terms"
        element={<Terms affiliate={affiliate?.memberValue} />}
      />
      <Route
        path="/privacy"
        element={<Privacy affiliate={affiliate?.memberValue} />}
      />
      <Route path="/*" element={<Navigate to="/delinquent" />} />
    </Routes>
  ) : (
    <Routes>
      <Route
        element={
          <LeftSidebar
            setOpenFeedbackTour={setOpenFeedbackTour}
            hideBackButton={
              activeOrg?.isRealeflow &&
              !isPropertySearchRetired &&
              location.pathname === '/property-search'
            }
            collapsed={
              activeOrg?.isRealeflow &&
              !isPropertySearchRetired &&
              location.pathname === '/property-search'
            }
          />
        }
      >
        <Route path="/launchpad" element={<LaunchpadPage />} />
        <Route path="/tools" element={<ToolsPage />} />
        <Route path="/marketplace" element={<MarketplacePage />} />
        <Route
          path="/contacts"
          element={
            me?.activeOrg?.member === 'pep' ? (
              <Suspense fallback={<SuspenseLoading />}>
                <ContactsPage />
              </Suspense>
            ) : (
              <Navigate to={defaultPage} />
            )
          }
        />
        <Route
          path="/submitted-marketplace"
          element={<MarketplacePage submitted />}
        />
        <Route
          path="/settings/marketplace"
          element={<MarketplaceSettingsPage />}
        />
        <Route
          path="/reports"
          element={
            me.housefoliosAdmin ? (
              <ReportsPage />
            ) : me.activeOrg.isAffiliate ? (
              <Navigate to="/affiliate-reports" replace />
            ) : (
              <Navigate to={defaultPage} />
            )
          }
        />
        <Route
          path="/reports2"
          element={
            <Navigate
              to={
                me.activeOrg.isAffiliate
                  ? '/affiliate-reports'
                  : '/reports'
              }
              replace
            />
          }
        />
        <Route
          path="/affiliate-reports"
          element={
            me.activeOrg.isAffiliate ? (
              <ReportsPage affiliateView />
            ) : me.housefoliosAdmin ? (
              <Navigate to="/reports" replace />
            ) : (
              <Navigate to={defaultPage} />
            )
          }
        />
        <Route
          path="/clients"
          element={
            me.housefoliosAdmin ? (
              <HouseFoliosAdminClientPage />
            ) : me.activeOrg.isAffiliate ? (
              <AffiliateAdminClientPage />
            ) : (
              <Navigate to={defaultPage} />
            )
          }
        />
        <Route
          path="/housefolios-admin/clients"
          element={
            me.housefoliosAdmin ? (
              <HouseFoliosAdminClientPage />
            ) : (
              <Navigate to={defaultPage} />
            )
          }
        />
        <Route
          path="/affiliate-admin/clients"
          element={
            me.activeOrg.isAffiliate ? (
              <AffiliateAdminClientPage />
            ) : (
              <Navigate to={defaultPage} />
            )
          }
        />
        <Route
          path="/affiliate-admin/missing-ghl-contact"
          element={
            me.activeOrg.isAffiliate ? (
              <MissingGhlContactPage />
            ) : (
              <Navigate to={defaultPage} />
            )
          }
        />
        <Route
          path="/affiliate-admin/realeflow-audit"
          element={
            me.activeOrg.isAffiliate &&
            me.activeOrg.isAffiliate.isRealeflow ? (
              <Suspense fallback={<SuspenseLoading />}>
                <RealeflowAuditPage />
              </Suspense>
            ) : (
              <Navigate to={defaultPage} />
            )
          }
        />
        <Route
          path="/partnerportal"
          element={
            me.activeOrg.isAffiliate ? (
              <PartnerPortal />
            ) : (
              <Navigate to={defaultPage} />
            )
          }
        />
        <Route
          path="/featureRequests"
          element={
            me.activeOrg.isAffiliate &&
            me.activeOrg.isAffiliate.isRealeflow ? (
              <FeatureRequest />
            ) : (
              <Navigate to={defaultPage} />
            )
          }
        />
        <Route
          path="/realeflow-audit"
          element={<Navigate to={defaultPage} />}
        />

        {/* Nested settings routes */}
        <Route path="/settings" element={<Outlet />}>
          <Route
            path="organization"
            element={<OrganizationSettingsPage />}
          />
          <Route path="personal" element={<PersonalSettingsPage />} />
          <Route
            path="assumptions"
            element={<AssumptionsSettingsPage />}
          />
          <Route path="billing" element={<BillingSettingsPage />} />
          <Route path="team" element={<TeamSettingsPage />} />
          {/*
          <Route path="integrations" element={<IntegrationsPage />} />
          */}
        </Route>

        <Route path="/my-properties" element={<PropertiesPage />} />
        <Route
          path="/submitted-lender-properties"
          element={<PropertiesPage lender />}
        />
        <Route
          path="/submitted-jv-properties"
          element={<PropertiesPage jv />}
        />
        <Route
          path="/shared-properties"
          element={<PropertiesPage shared />}
        />
        <Route path="/my-lots" element={<LotsPage />} />
        <Route path="/board" element={<BoardPage />} />
        <Route path="/lot" element={<LotPage />} />
        <Route path="/commercial" element={<CommercialPage />} />
        <Route
          path="/commercial-property-details/:id/:view"
          element={<CommercialProperty />}
        />
        <Route
          path="/property-search"
          element={
            me.activeOrg.isRealeflow ? (
              <RealeflowRedirect />
            ) : (
              <PropertySearchUnavailablePage />
            )
          }
        />
        <Route path="/trash" element={<TrashPage />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/coaching-offer" element={<CoachingOffer />} />
        <Route
          path="/coaching"
          element={<Navigate to="/coaching-offer" replace />}
        />
        <Route path="/training" element={<TrainingPage />} />
        <Route path="/support-chat" element={<SupportChatPage />} />
        <Route
          path="/dealsauce-buybox"
          element={
            <Suspense fallback={<SuspenseLoading />}>
              <DealSauceBuyBoxPage />
            </Suspense>
          }
        />

        <Route path="/articles/:id" element={<ArticlePage />} />

        {/* Nested settings routes */}
        <Route path="/settings" element={<Outlet />}>
          <Route
            path="organization"
            element={<OrganizationSettingsPage />}
          />
          <Route path="personal" element={<PersonalSettingsPage />} />
          <Route
            path="assumptions"
            element={<AssumptionsSettingsPage />}
          />
          <Route path="billing" element={<BillingSettingsPage />} />
          <Route path="team" element={<TeamSettingsPage />} />
        </Route>

        <Route
          path="/sharedPortfolio"
          element={<SharedPortfolio />}
        />
        <Route
          path="/sharedMarketplace"
          element={<SharedMarketplace />}
        />
        <Route path="/sharedProperty" element={<SharedProperty />} />
        <Route
          path="/sharedProperties"
          element={<SharedProperties />}
        />
        <Route
          path="/property-details/:id/proforma/:type"
          element={<PDFContainer />}
        />
        <Route
          path="/deal-analysis"
          element={
            <Suspense fallback={<LoadingSkeleton variant="page" />}>
              <DealAnalysisPage />
            </Suspense>
          }
        />
        <Route
          path="/housefolios-admin"
          element={
            me.housefoliosAdmin ? (
              <HouseFoliosAdminPage />
            ) : (
              <Navigate to={defaultPage} />
            )
          }
        />
      </Route>

      <Route
        element={
          <LeftSidebar
            collapsed
            setOpenFeedbackTour={setOpenFeedbackTour}
          />
        }
      >
        <Route
          path="/property-details/:id/:view"
          element={
            <PropertyPage
              setIsOpen={setOpenTour}
              setIsOpenVerifyFinancialsTour={
                setOpenVerifyFinancialsTour
              }
              setIsOpenAnalyzeCompsTour={setOpenAnalyzeCompsTour}
              setIsOpenChooseStrategyTour={setOpenChooseStrategyTour}
              setIsOpenAnalyzeAreaTour={setOpenAnalyzeAreaTour}
              setIsOpenTakeActionTour={setOpenTakeActionTour}
            />
          }
        />
        <Route path="/:id/editLot" element={<LotPage />} />
      </Route>

      {/* Redirects */}
      <Route
        path="/property/:id/proforma/:type"
        element={<ProformaRedirect isLoggedIn={true} />}
      />
      <Route
        path="/*"
        element={
          <Navigate
            to={
              activeOrg?.isRealeflow && !isPropertySearchRetired
                ? '/property-search'
                : defaultPage
            }
          />
        }
      />
      <Route
        path="/legal"
        element={<Legal affiliate={affiliate?.memberValue} />}
      />
      <Route
        path="/terms"
        element={<Terms affiliate={affiliate?.memberValue} />}
      />
      <Route
        path="/privacy"
        element={<Privacy affiliate={affiliate?.memberValue} />}
      />
    </Routes>
  )
}

export default SignedIn
